import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ROUTES, useIsDesktop } from '@/components/gg/chrome'
import { useClearTakes, useLibraryStories, useSetStoryProgress } from '@/hooks/useLibrary'
import { LibraryRow, dayLabel, fmtBytes, type RowAction } from '@/components/library/LibraryRow'
import { saveTakeToPhotos } from '@/components/library/saveToPhotos'
import type { LibraryStory } from '@/types/library'
import '@/styles/story2.css'
import '@/styles/library2.css'

/**
 * Library (Brian's after/library.html v2.1, Oct 21).
 *
 * Every story this account built, and any still being written. A story moves
 * on as it's done (Brian: "so they don't fall into the trap where they have
 * this long list … and can't tell which ones they've already done"):
 *   To record  built, not recorded; built over a week ago sits apart
 *   Recorded   Save to Photos, I posted it, Record again
 *   Posted     the archive: a light record (the story, its caption, the day)
 *              that clears itself 90 days after I posted it
 *   Newsletter in this week's issue
 * Videos live in the phone's Photos, so the Library tracks stories, not files.
 */

type FilterKey = 'todo' | 'recorded' | 'posted' | 'nl'
const FILTERS: [FilterKey, string, (s: LibraryStory) => boolean][] = [
  ['todo', 'To record', (s) => s.stage === 'todo' || s.stage === 'writing'],
  ['recorded', 'Recorded', (s) => s.stage === 'recorded'],
  ['posted', 'Posted', (s) => s.stage === 'posted'],
  ['nl', 'Newsletter', (s) => s.inNewsletter],
]
const NONE: Record<FilterKey, [string, string]> = {
  todo: ['Nothing waiting to record.', 'Build a story from your feed and it shows up here.'],
  recorded: ['Nothing waiting to post.', 'Record a story and it waits here until you post it.'],
  posted: ['Nothing posted yet.', 'After you post a take, tap I posted it and the story is filed here.'],
  nl: ['None of these are in this week’s issue.', 'Open a story and tap ＋ Newsletter to add it.'],
}
const STORE_BUDGET = 2 * 1024 ** 3
const FILTER_KEY = 'gogrov.library.filter'
const isFilter = (f: string | null): f is FilterKey => FILTERS.some((x) => x[0] === f)

export function Library() {
  const navigate = useNavigate()
  const desktop = useIsDesktop()
  const [params] = useSearchParams()
  const { stories: all, isLoading, isError } = useLibraryStories()
  const setProgress = useSetStoryProgress()
  const clearTakes = useClearTakes()

  const [filter, setFilter] = useState<FilterKey>(() => {
    const q = params.get('f'); if (isFilter(q)) return q
    try { const f = localStorage.getItem(FILTER_KEY); if (isFilter(f)) return f } catch { /* private mode */ }
    return 'todo'
  })
  const pick = (f: FilterKey) => { setFilter(f); try { localStorage.setItem(FILTER_KEY, f) } catch { /* private mode */ } }

  const [armed, setArmed] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [toast, setToast] = useState<ReactNode>(null)
  const toastT = useRef(0)
  const say = (t: ReactNode) => { setToast(t); window.clearTimeout(toastT.current); toastT.current = window.setTimeout(() => setToast(null), 3200) }
  useEffect(() => () => window.clearTimeout(toastT.current), [])

  const patch = (id: string, p: Parameters<typeof setProgress.mutateAsync>[0]['patch']) => setProgress.mutateAsync({ storyId: id, patch: p })
  const nowIso = () => new Date().toISOString()

  async function act(id: string, fn: () => Promise<void>) {
    if (busy) return
    setBusy(id)
    try { await fn() } catch { say('That did not go through. Try it again.') } finally { setBusy(null) }
  }

  // Save to Photos: the latest take (the app saves to Photos; the web downloads it).
  const save = (s: LibraryStory) => act(s.id, async () => {
    await saveTakeToPhotos(s.id, s.headline, s.story?.caption)
    await patch(s.id, { saved_at: nowIso() })
    say(<>Saved to Photos. <b>Post it from there</b>, then tap I posted it.</>)
  })
  // I posted it: filed under Posted; once it's in Photos, GoGrov's copy goes (Brian: no backups on the app).
  const posted = (s: LibraryStory) => {
    if (s.takes > 0 && !s.savedAt) { say(<><b>Save it to Photos first.</b> That’s where TikTok and Instagram find it.</>); return }
    void act(s.id, async () => {
      await patch(s.id, { posted_at: nowIso(), to_phone_at: null })
      if (s.takes > 0) { await clearTakes.mutateAsync(s.id); say(<>Filed under Posted. <b>GoGrov’s copy is removed</b>; the video is in your Photos.</>) }
      else say('Filed under Posted.')
    })
  }
  const removeCopy = (s: LibraryStory) => act(s.id, async () => { await clearTakes.mutateAsync(s.id); say('GoGrov’s copy removed.') })
  const del = (s: LibraryStory) => {
    if (armed !== s.id) { setArmed(s.id); window.setTimeout(() => setArmed((a) => (a === s.id ? null : a)), 3500); return }
    setArmed(null)
    void act(s.id, async () => { if (s.takes > 0) await clearTakes.mutateAsync(s.id); await patch(s.id, { hidden_at: nowIso() }) })
  }
  const record = (s: LibraryStory) => navigate(`${ROUTES.record}?story=${encodeURIComponent(s.id)}`)

  function actionsFor(s: LibraryStory): RowAction[] {
    const b = busy === s.id
    if (s.stage === 'writing') return []
    if (s.stage === 'todo') return [{ key: 'rec', label: 'Record', primary: true, onClick: () => record(s) }]
    if (s.stage === 'recorded') {
      // Oct 22 (Brian: "the desktop needs to use the camera to record as well"): Record works on a computer too.
      const again: RowAction = { key: 'again', label: 'Record again', onClick: () => record(s) }
      return s.takes > 0 && !s.savedAt
        ? [{ key: 'save', label: 'Save to Photos', primary: true, busy: b, onClick: () => void save(s) }, { key: 'post', label: 'I posted it', busy: b, onClick: () => posted(s) }]
        : [{ key: 'post', label: 'I posted it', primary: true, busy: b, onClick: () => posted(s) }, again]
    }
    return [
      ...(s.takes > 0 ? [{ key: 'rm', label: 'Remove GoGrov’s copy', busy: b, onClick: () => void removeCopy(s) }] : []),
      { key: 'del', label: armed === s.id ? 'Tap again to delete' : 'Delete', danger: true, armed: armed === s.id, busy: b, onClick: () => del(s) },
    ]
  }

  if (isLoading) return <div className="libx"><p className="libx__sub">Opening your Library.</p></div>
  if (isError) return <div className="libx"><div className="libx__none"><b>Could not load your Library.</b>Try again in a moment.</div></div>

  // A story is built the moment you build it (desk v7.1: "in Library and counted at once").
  const rec = all.filter((s) => s.recordedAt).length
  const sub = all.length ? `${all.length} ${all.length === 1 ? 'story' : 'stories'} built${rec ? ` · ${rec} recorded` : ''}` : 'The stories you build live here.'

  if (!all.length) {
    return (
      <div className="libx">
        {!desktop && <p className="libx__sub">{sub}</p>}
        <div className="libx__empty">
          <div className="libx__ico"><svg viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2" /><path d="M4 10h16M10 10v10" /></svg></div>
          <h2>Nothing built yet</h2>
          <p>Build a story from your feed and it lands here.</p>
          <Link className="libx__go" to={ROUTES.feed}>Go to your feed<svg viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg></Link>
        </div>
        <Toast text={toast} />
      </div>
    )
  }

  const shown = all.filter(FILTERS.find((x) => x[0] === filter)![2])
  const total = all.reduce((n, s) => n + s.takeBytes, 0)
  const full = total >= STORE_BUDGET
  const meter = (filter === 'posted' || full) && (
    <div className={`libx__space${full ? ' is-full' : ''}`}>
      <div className="libx__spk"><span>{full ? 'GoGrov’s space on this phone is full' : 'GoGrov’s space on this phone'}</span><span>{fmtBytes(total)} of {fmtBytes(STORE_BUDGET)}</span></div>
      <div className="libx__spbar"><i style={{ width: `${Math.min(100, (total / STORE_BUDGET) * 100).toFixed(1)}%` }} /></div>
      <p>{full
        ? 'Delete posted stories you’re done with to record again. Save a take to Photos first if you want to keep it.'
        : 'Your videos live in your Photos. Posted keeps the story, its caption and the day you posted it, for 90 days. Takes still on GoGrov count against this space.'}</p>
    </div>
  )

  // Grouped by the day it was built (Posted: the day it was posted); in To
  // record, a story built over a week ago and never recorded sits apart.
  const at = (s: LibraryStory) => (filter === 'posted' ? s.postedAt ?? s.at : s.at)
  const keyOf = (s: LibraryStory) => (filter === 'todo' && s.stale && !s.toPhoneAt ? 'Not recorded · older than a week' : dayLabel(at(s)))
  const sorted = shown.slice().sort((a, b) => (filter === 'todo' ? Number(a.stale) - Number(b.stale) : 0) || at(b) - at(a))
  const groups: { k: string; items: LibraryStory[] }[] = []
  sorted.forEach((s) => { const k = keyOf(s), g = groups[groups.length - 1]; if (g && g.k === k) g.items.push(s); else groups.push({ k, items: [s] }) })

  return (
    <div className="libx">
      {!desktop && <p className="libx__sub">{sub}</p>}
      <div className="libx__filters" role="group" aria-label="Show">
        {FILTERS.map(([k, name, fn]) => (
          <button key={k} type="button" className="libx__f" aria-pressed={k === filter} onClick={() => pick(k)}>
            {name}<span className="libx__n">{all.filter(fn).length}</span>
          </button>
        ))}
      </div>
      {meter}
      {!shown.length ? (
        <div className="libx__none"><b>{NONE[filter][0]}</b>{NONE[filter][1]}</div>
      ) : groups.map((g) => (
        <section key={g.k}>
          <div className="libx__day">{g.k}</div>
          <div className="libx__grid">{g.items.map((s) => <LibraryRow key={s.id} s={s} actions={actionsFor(s)} />)}</div>
        </section>
      ))}
      <Toast text={toast} />
    </div>
  )
}

function Toast({ text }: { text: ReactNode }) {
  return <div className={`libx__toast${text ? ' is-on' : ''}`} role="status">{text}</div>
}

export default Library
